import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Landmark, Receipt, FileCheck2, ShieldCheck, Fingerprint, BadgeCheck, XCircle, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { icon: Landmark, title: "What Pramaana verifies", body: "Pramaana connects a devotee's Sankalpa with a verified temple, temple-approved seva, transparent money flow, fulfilment evidence and authorised validation — creating one traceable Verified Record. Pramaana verifies the trust chain surrounding the seva. It does not verify belief or religious outcome." },
  { icon: Receipt, title: "Payment is not fulfilment", body: "A devotee's payment is synthetic here and, even when real, proves only that money moved. It never proves the seva was actually performed. Booking starts at BOOKED — not CERTIFIED." },
  { icon: FileCheck2, title: "Evidence is submitted, not trusted", body: "The temple records fulfilment and attaches evidence (a receipt, a statement, a photo). Uploading evidence does not accept it — it simply enters the record for review." },
  { icon: ShieldCheck, title: "Validation decides", body: "An authorised validator reviews the evidence against the temple's required-evidence rules. Only then can a record be certified — and it can be refused." },
  { icon: Fingerprint, title: "Integrity ≠ religious proof", body: "SHA-256 hashes the canonical record and evidence bytes so any later change is detectable. It proves the record is tamper-evident — never that a religious act occurred." },
  { icon: BadgeCheck, title: "See it: Certified", body: "The full chain complete — verified temple, fulfilment, all required evidence accepted, validated, and hashed. This is what an earned Verified Record looks like.", go: "/records/PRM-DEMOCERT01", goLabel: "Open the Certified record" },
  { icon: XCircle, title: "See it: Certification Withheld", body: "Fulfilment was recorded and payment succeeded — but a required piece of evidence was missing. Certification is withheld, with a clear reason. Refusal is the point.", go: "/records/PRM-DEMOHOLD02", goLabel: "Open the Withheld record" },
];

export default function JudgeTour({ open, onOpenChange }) {
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const s = STEPS[i];
  const last = i === STEPS.length - 1;

  const goTo = (path) => { onOpenChange(false); navigate(path); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setI(0); }}>
      <DialogContent className="max-w-lg bg-white rounded-sm p-0 overflow-hidden" data-testid="judge-tour">
        <div className="bg-slate-900 text-slate-100 px-7 py-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">3-minute tour · step {i + 1} of {STEPS.length}</div>
          <div className="flex items-center gap-3 mt-3">
            <s.icon size={22} strokeWidth={1.5} className="text-emerald-400 shrink-0" />
            <DialogTitle className="font-serif text-2xl leading-tight font-normal" data-testid="tour-title">{s.title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">A guided introduction to how Pramaana verifies a Sankalpa through evidence, validation and record integrity.</DialogDescription>
        </div>
        <div className="px-7 py-6">
          <p className="text-sm text-slate-600 leading-relaxed min-h-[84px]" data-testid="tour-body">{s.body}</p>
          {s.go && (
            <Button onClick={() => goTo(s.go)} data-testid="tour-go"
              className="mt-2 rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
              {s.goLabel} <ArrowRight size={15} />
            </Button>
          )}
          <div className="flex items-center gap-2 mt-6">
            {STEPS.map((_, idx) => (
              <span key={idx} className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-slate-900" : "w-2 bg-slate-200"}`} />
            ))}
            <div className="ml-auto flex gap-2">
              {i > 0 && <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => setI(i - 1)} data-testid="tour-prev"><ArrowLeft size={14} /> Back</Button>}
              {!last
                ? <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => setI(i + 1)} data-testid="tour-next">Next <ArrowRight size={14} /></Button>
                : <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => { onOpenChange(false); setI(0); }} data-testid="tour-finish">Finish</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
